import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    BasePost,
    CreateCollectionData,
    ReportData,
    Steps,
    UseCreateCollectionNavigationProps,
} from '../typings';
import { useDialogSteps } from './useDialogSteps';

interface LocationState {
    step?: Steps;
    reportData?: ReportData;
    createCollectionData?: CreateCollectionData;
}

export const useCreateCollectionNavigation = ({
    setSelectedPosts,
}: UseCreateCollectionNavigationProps) => {
    const { state } = useLocation();
    const locationState = state as LocationState | undefined;

    const [step, setStep] = useState<Steps>(locationState?.step || Steps.SEARCH);
    const [previewPost, setPreviewPost] = useState<BasePost | undefined>();
    const [reportData, setReportData] = useState<ReportData>(
        locationState?.reportData || { targetId: '', targetType: 'post' },
    );
    const [createCollectionData, setCreateCollectionData] = useState<CreateCollectionData>(
        locationState?.createCollectionData || { postIds: [] },
    );

    const onStepChange = useCallback(
        (newStep: Steps, additionalData?: Record<string, unknown>) => {
            setStep(newStep);

            if (newStep === Steps.PREVIEW && additionalData?.post) {
                const post = additionalData.post as BasePost;
                setPreviewPost(post);
            } else if (newStep === Steps.REPORT && additionalData?.reportData) {
                const data = additionalData.reportData as ReportData;
                setReportData(data);
            } else if (
                newStep === Steps.CREATE_COLLECTION &&
                additionalData?.createCollectionData
            ) {
                const data = additionalData.createCollectionData as CreateCollectionData;
                setCreateCollectionData(data);
            } else if (newStep === Steps.SEARCH) {
                setPreviewPost(undefined);
                setReportData({ targetId: '', targetType: 'post' });
                setCreateCollectionData({ postIds: [] });
            }
        },
        [setStep, setPreviewPost, setReportData, setCreateCollectionData],
    );

    const dialogNavigation = useDialogSteps(Steps.SEARCH, onStepChange);

    const goToPreview = useCallback(
        (post: BasePost) => {
            dialogNavigation.goToStep(Steps.PREVIEW, { post });
        },
        [dialogNavigation],
    );

    const openReport = useCallback(
        (postId: string) => {
            const data: ReportData = { targetId: postId, targetType: 'post' };
            dialogNavigation.goToStep(Steps.REPORT, { reportData: data });
        },
        [dialogNavigation],
    );

    const openCreateCollection = useCallback(
        (postIds: string[]) => {
            const data: CreateCollectionData = { postIds };
            dialogNavigation.goToStep(Steps.CREATE_COLLECTION, { createCollectionData: data });
        },
        [dialogNavigation],
    );

    const handleReportSuccess = useCallback(() => {
        if (reportData.targetId) {
            setSelectedPosts((prev: string[]) =>
                prev.filter((id: string) => id !== reportData.targetId),
            );
        }

        dialogNavigation.navigateBack();
        dialogNavigation.replaceStep(Steps.SEARCH);
    }, [reportData.targetId, dialogNavigation, setSelectedPosts]);

    return {
        step,
        previewPost,
        reportData,
        createCollectionData,
        goToPreview,
        openReport,
        openCreateCollection,
        navigateBack: dialogNavigation.navigateBack,
        handleReportSuccess,
    };
};
